/**
 * Treeselect Data Helper Functions
 * ฟังก์ชันช่วยในการแปลงข้อมูลให้เหมาะสมกับ Treeselect component
 */

/**
 * แปลงข้อมูลแบบ flat ที่มี parent_id ให้เป็น hierarchical structure
 * @param {Array} flatData - ข้อมูลแบบ flat ที่มี parent_id
 * @param {String} parentIdKey - ชื่อ field ที่เก็บ parent id (default: 'parent_id')
 * @param {String} idKey - ชื่อ field ที่เก็บ id (default: 'id')
 * @param {String} textKey - ชื่อ field ที่เก็บ text (default: 'name')
 * @returns {Array} ข้อมูลแบบ hierarchical
 */
function convertFlatToHierarchical(flatData, parentIdKey = 'parent_id', idKey = 'id', textKey = 'name') {
  if (!Array.isArray(flatData)) return [];

  const result = [];
  const lookup = {};

  // สร้าง lookup object
  flatData.forEach(item => {
    lookup[item[idKey]] = {
      id: item[idKey],
      text: item[textKey],
      raw: item,
      children: []
    };
  });

  // สร้าง tree structure
  flatData.forEach(item => {
    const node = lookup[item[idKey]];
    const parentId = item[parentIdKey];

    if (parentId && lookup[parentId]) {
      lookup[parentId].children.push(node);
    } else {
      result.push(node);
    }
  });

  // ลบ children array ที่ว่าง
  function cleanEmptyChildren(nodes) {
    nodes.forEach(node => {
      if (node.children && node.children.length === 0) {
        delete node.children;
      } else if (node.children) {
        cleanEmptyChildren(node.children);
      }
    });
  }

  cleanEmptyChildren(result);
  return result;
}

/**
 * แปลงข้อมูลที่มี level field ให้เป็น hierarchical structure
 * @param {Array} levelData - ข้อมูลที่มี level field
 * @param {String} levelKey - ชื่อ field ที่เก็บ level (default: 'level')
 * @param {String} idKey - ชื่อ field ที่เก็บ id (default: 'id')
 * @param {String} textKey - ชื่อ field ที่เก็บ text (default: 'name')
 * @returns {Array} ข้อมูลแบบ hierarchical
 */
function convertLevelToHierarchical(levelData, levelKey = 'level', idKey = 'id', textKey = 'name') {
  if (!Array.isArray(levelData)) return [];

  const result = [];
  const stack = []; // Stack to keep track of parent nodes at each level

  levelData.forEach(item => {
    const currentLevel = item[levelKey] || 0;
    const node = {
      id: item[idKey],
      text: item[textKey],
      level: currentLevel,
      raw: item,
      children: []
    };

    // Adjust stack size to current level
    while (stack.length > currentLevel) {
      stack.pop();
    }

    if (currentLevel === 0) {
      // Root level
      result.push(node);
      stack[0] = node;
    } else if (stack[currentLevel - 1]) {
      // Child level
      stack[currentLevel - 1].children.push(node);
      stack[currentLevel] = node;
    }
  });

  // ลบ children array ที่ว่าง
  function cleanEmptyChildren(nodes) {
    nodes.forEach(node => {
      if (node.children && node.children.length === 0) {
        delete node.children;
      } else if (node.children) {
        cleanEmptyChildren(node.children);
      }
    });
  }

  cleanEmptyChildren(result);
  return result;
}

/**
 * เพิ่ม visual cues และ indentation ให้กับข้อมูล
 * @param {Array} data - ข้อมูลแบบ hierarchical
 * @param {Object} options - ตัวเลือกการแสดงผล
 * @returns {Array} ข้อมูลที่มี visual cues
 */
function addVisualCues(data, options = {}) {
  const defaultOptions = {
    showIcons: true,
    showLines: true,
    indentSize: 2,
    branchIcon: '📁',
    leafIcon: '📄',
    lineChar: '├─',
    lastLineChar: '└─',
    verticalChar: '│'
  };

  const opts = { ...defaultOptions, ...options };

  function processNode(node, level = 0, isLast = false, prefix = '') {
    const isLeaf = !node.children || node.children.length === 0;
    let nodePrefix = '';

    if (level === 0) {
      nodePrefix = opts.showIcons ? (isLeaf ? opts.leafIcon + ' ' : opts.branchIcon + ' ') : '';
    } else {
      const lineChar = isLast ? opts.lastLineChar : opts.lineChar;
      const icon = opts.showIcons ? (isLeaf ? opts.leafIcon : opts.branchIcon) : '';
      nodePrefix = prefix + lineChar + ' ' + (icon ? icon + ' ' : '');
    }

    const processedNode = {
      ...node,
      text: nodePrefix + node.text,
      level: level
    };

    if (node.children && node.children.length > 0) {
      const childPrefix = level === 0 ? '' : prefix + (isLast ? '  ' : opts.verticalChar + ' ');
      processedNode.children = node.children.map((child, index) => {
        const isLastChild = index === node.children.length - 1;
        return processNode(child, level + 1, isLastChild, childPrefix);
      });
    }

    return processedNode;
  }

  return data.map((node, index) => {
    const isLast = index === data.length - 1;
    return processNode(node, 0, isLast);
  });
}

/**
 * แปลงข้อมูลจาก Laravel collection/array ให้เหมาะสมกับ Treeselect
 * @param {Array} laravelData - ข้อมูลจาก Laravel
 * @param {Object} mapping - การ mapping field names
 * @returns {Array} ข้อมูลที่พร้อมใช้กับ Treeselect
 */
function formatForTreeselect(laravelData, mapping = {}) {
  const defaultMapping = {
    id: 'id',
    text: 'name',
    parent_id: 'parent_id',
    level: 'level',
    children: 'children'
  };

  const map = { ...defaultMapping, ...mapping };

  function formatNode(item) {
    const formatted = {
      id: item[map.id],
      text: item[map.text],
      raw: item
    };

    // เพิ่ม children ถ้ามี
    if (item[map.children] && Array.isArray(item[map.children])) {
      formatted.children = item[map.children].map(formatNode);
    }

    // เพิ่ม level ถ้ามี
    if (item[map.level] !== undefined) {
      formatted.level = item[map.level];
    }

    return formatted;
  }

  return laravelData.map(formatNode);
}

/**
 * สร้างข้อมูลตัวอย่างสำหรับทดสอบ
 * @param {Number} levels - จำนวนระดับ
 * @param {Number} itemsPerLevel - จำนวน items ในแต่ละระดับ
 * @returns {Array} ข้อมูลตัวอย่าง
 */
function generateSampleData(levels = 3, itemsPerLevel = 3) {
  function generateLevel(level, parentId = null, parentText = '') {
    const items = [];
    for (let i = 1; i <= itemsPerLevel; i++) {
      const id = parentId ? `${parentId}${i}` : `${i}`;
      const text = parentId ? `${parentText} - รายการ ${i}` : `หมวดหมู่ ${i}`;
      
      const item = {
        id: id,
        text: text,
        level: level
      };

      if (level < levels - 1) {
        item.children = generateLevel(level + 1, id, text);
      }

      items.push(item);
    }
    return items;
  }

  return generateLevel(0);
}

/**
 * ค้นหาข้อมูลใน tree structure
 * @param {Array} data - ข้อมูล tree
 * @param {String} searchTerm - คำค้นหา
 * @param {String} searchField - field ที่ต้องการค้นหา (default: 'text')
 * @returns {Array} ผลลัพธ์การค้นหา
 */
function searchTreeData(data, searchTerm, searchField = 'text') {
  if (!searchTerm) return data;

  const result = [];
  const term = searchTerm.toLowerCase();

  function searchNode(node) {
    const matches = node[searchField] && node[searchField].toLowerCase().includes(term);
    let matchingChildren = [];

    if (node.children) {
      matchingChildren = node.children.map(searchNode).filter(Boolean);
    }

    if (matches || matchingChildren.length > 0) {
      return {
        ...node,
        children: matchingChildren.length > 0 ? matchingChildren : undefined
      };
    }

    return null;
  }

  data.forEach(node => {
    const matchedNode = searchNode(node);
    if (matchedNode) {
      result.push(matchedNode);
    }
  });

  return result;
}

/**
 * หาเส้นทาง (path) ของ node ที่เลือก
 * @param {Array} data - ข้อมูล tree
 * @param {String|Number} targetId - id ที่ต้องการหา path
 * @returns {Array} array ของ nodes ที่เป็น path
 */
function findNodePath(data, targetId) {
  function searchPath(nodes, path = []) {
    for (let node of nodes) {
      const currentPath = [...path, node];
      
      if (node.id === targetId) {
        return currentPath;
      }
      
      if (node.children) {
        const result = searchPath(node.children, currentPath);
        if (result) return result;
      }
    }
    return null;
  }

  return searchPath(data) || [];
}

// Export functions for use in Vue components
export {
  convertFlatToHierarchical,
  convertLevelToHierarchical,
  addVisualCues,
  formatForTreeselect,
  generateSampleData,
  searchTreeData,
  findNodePath
};

// Usage examples:
/*
// 1. แปลงข้อมูล flat ที่มี parent_id
const flatData = [
  { id: 1, name: 'หมวดหมู่ A', parent_id: null },
  { id: 2, name: 'หมวดหมู่ B', parent_id: 1 },
  { id: 3, name: 'หมวดหมู่ C', parent_id: 1 },
  { id: 4, name: 'หมวดหมู่ D', parent_id: 2 }
];
const hierarchicalData = convertFlatToHierarchical(flatData);

// 2. แปลงข้อมูลที่มี level
const levelData = [
  { id: 1, name: 'ระดับ 1', level: 0 },
  { id: 2, name: 'ระดับ 2', level: 1 },
  { id: 3, name: 'ระดับ 3', level: 2 }
];
const hierarchicalFromLevel = convertLevelToHierarchical(levelData);

// 3. เพิ่ม visual cues
const dataWithCues = addVisualCues(hierarchicalData, {
  showIcons: true,
  showLines: true
});

// 4. ใช้ใน Vue component
export default {
  data() {
    return {
      treeselectOptions: []
    };
  },
  async mounted() {
    const response = await axios.get('/api/categories');
    this.treeselectOptions = formatForTreeselect(response.data);
  }
};
*/
